(() => {
  if (customElements.get('tt-gift-bag')) return;

  class TTGiftBag extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.addButton = this.querySelector('[data-tt-gift-add]');
      this.designs = Array.from(this.querySelectorAll('[data-tt-gift-variant]'));
      this.sizes = Array.from(this.querySelectorAll('[data-tt-gift-size]'));
      this.lines = this.querySelector('[data-tt-gift-lines]');
      this.lineTemplate = this.querySelector('[data-tt-gift-line-template]');
      if (!this.addButton || !this.designs.length || !this.lines || !this.lineTemplate) return;
      this.initialized = true;
      this.remembered = new Map();
      this.quantities = new Map();
      this.designById = new Map(this.designs.map(button => [button.dataset.ttGiftVariant, button]));
      this.guide = this.querySelector('[data-tt-gift-guide]');
      this.photo = this.querySelector('[data-tt-gift-photo]');
      this.caption = this.querySelector('[data-tt-gift-caption]');
      this.priceLabel = this.querySelector('[data-tt-gift-price]');
      this.compareLabel = this.querySelector('[data-tt-gift-compare]');
      this.unavailableLabel = this.querySelector('[data-tt-gift-unavailable]');
      this.selectionPanel = this.querySelector('[data-tt-gift-selection]');
      this.totalLabel = this.querySelector('[data-tt-gift-total]');
      this.summaryCount = this.querySelector('[data-tt-gift-count]');
      this.status = this.querySelector('[data-tt-gift-status]');
      this.moneyFormat = this.readMoneyFormat();

      this.addEventListener('click', event => {
        const control = event.target.closest('button');
        if (!control || !this.contains(control) || control.disabled) return;
        if (control.hasAttribute('data-tt-gift-size')) this.selectSize(control.dataset.ttGiftSize);
        else if (control.hasAttribute('data-tt-gift-variant')) this.selectVariant(control);
        else if (control === this.addButton && this.current) this.changeQuantity(this.current.dataset.ttGiftVariant, 1);
        else {
          const row = control.closest('[data-tt-gift-line]');
          if (!row) return;
          const id = row.dataset.ttGiftLine;
          if (control.hasAttribute('data-tt-gift-increase')) this.changeQuantity(id, 1);
          else if (control.hasAttribute('data-tt-gift-decrease')) this.changeQuantity(id, -1);
          else if (control.hasAttribute('data-tt-gift-remove')) this.changeQuantity(id, -(this.quantities.get(id) || 0));
        }
      });
      this.addEventListener('keydown', event => this.handleKeydown(event));

      const initial = this.designById.get(this.dataset.ttGiftInitial);
      this.selectVariant(initial || this.designs.find(button => this.isAvailable(button)) || this.designs[0]);
      this.renderSelections();
    }

    isAvailable(button) {
      return Boolean(button && button.dataset.ttGiftAvailable === 'true');
    }

    selectSize(size) {
      const candidates = this.designs.filter(button => button.dataset.ttGiftSizeValue === size);
      const remembered = this.remembered.get(size);
      const next = candidates.find(button => button.dataset.ttGiftVariant === remembered && this.isAvailable(button))
        || candidates.find(button => this.isAvailable(button)) || candidates[0];
      if (next) this.selectVariant(next);
    }

    selectVariant(button) {
      if (!button || !this.designs.includes(button)) return;
      this.current = button;
      const size = button.dataset.ttGiftSizeValue;
      const available = this.isAvailable(button);
      this.remembered.set(size, button.dataset.ttGiftVariant);
      this.sizes.forEach(option => {
        const selected = option.dataset.ttGiftSize === size;
        option.setAttribute('aria-checked', String(selected));
        option.tabIndex = selected ? 0 : -1;
      });
      this.designs.forEach(option => {
        const selected = option === button;
        option.hidden = option.dataset.ttGiftSizeValue !== size;
        option.setAttribute('aria-checked', String(selected));
        option.tabIndex = selected && available ? 0 : -1;
      });
      if (this.caption) this.caption.textContent = button.dataset.ttGiftName;
      if (this.priceLabel) this.priceLabel.textContent = button.dataset.ttGiftMoney;
      if (this.compareLabel) {
        this.compareLabel.textContent = button.dataset.ttGiftCompareMoney;
        this.compareLabel.hidden = Number(button.dataset.ttGiftCompareValue) <= Number(button.dataset.ttGiftPriceValue);
      }
      if (this.unavailableLabel) this.unavailableLabel.hidden = available;
      if (this.photo) {
        let image = this.photo.querySelector('img');
        if (button.dataset.ttGiftImage) {
          if (!image) {
            image = document.createElement('img');
            image.width = 72;
            image.height = 72;
            image.loading = 'lazy';
            this.photo.appendChild(image);
          }
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = button.dataset.ttGiftImage;
          image.alt = size + ' / ' + button.dataset.ttGiftName;
          image.hidden = false;
        } else if (image) image.hidden = true;
      }
      this.updateAddButton();
      this.emitChange();
    }

    updateAddButton() {
      const count = this.current ? this.quantities.get(this.current.dataset.ttGiftVariant) || 0 : 0;
      this.addButton.disabled = !this.isAvailable(this.current) || count >= 99;
      if (count >= 99) this.addButton.title = this.dataset.ttGiftLimit;
      else this.addButton.removeAttribute('title');
    }

    changeQuantity(id, change) {
      const button = this.designById.get(String(id));
      if (!button || (change > 0 && !this.isAvailable(button))) return;
      const previous = this.quantities.get(String(id)) || 0;
      const quantity = Math.max(0, Math.min(99, previous + Math.trunc(change)));
      if (!Number.isFinite(quantity) || quantity === previous) return;
      const activeRow = document.activeElement && document.activeElement.closest('[data-tt-gift-line]');
      const removedFocus = quantity === 0 && activeRow && activeRow.dataset.ttGiftLine === String(id);
      if (quantity) this.quantities.set(String(id), quantity);
      else this.quantities.delete(String(id));
      this.renderSelections();
      if (removedFocus) {
        const next = this.lines.querySelector('[data-tt-gift-decrease]');
        (next || (this.addButton.disabled ? this.sizes.find(option => option.tabIndex === 0) : this.addButton)).focus();
      }
      if (this.status) this.status.textContent = button.dataset.ttGiftSizeValue + ' / ' + button.dataset.ttGiftName
        + '. ' + this.dataset.ttGiftLabelQuantity + ': ' + quantity + '.';
      this.emitChange();
    }

    renderSelections() {
      const selections = this.getSelections();
      const selectedIds = new Set(selections.map(selection => String(selection.id)));
      this.quantities.forEach((quantity, id) => { if (!selectedIds.has(id)) this.quantities.delete(id); });
      Array.from(this.lines.children).forEach(row => { if (!selectedIds.has(row.dataset.ttGiftLine)) row.remove(); });
      let total = 0;
      selections.forEach(selection => {
        const id = String(selection.id);
        const data = this.designById.get(id).dataset;
        const name = data.ttGiftSizeValue + ' / ' + data.ttGiftName;
        let row = Array.from(this.lines.children).find(element => element.dataset.ttGiftLine === id);
        if (!row) {
          row = this.lineTemplate.content.firstElementChild.cloneNode(true);
          row.dataset.ttGiftLine = id;
          const image = row.querySelector('img');
          if (data.ttGiftImage) image.src = data.ttGiftImage;
          else image.hidden = true;
          const sizeOption = this.sizes.find(option => option.dataset.ttGiftSize === data.ttGiftSizeValue);
          const sizeLabel = sizeOption.querySelector('[data-tt-gift-size-label]') || sizeOption;
          row.querySelector('[data-tt-gift-line-size]').textContent = sizeLabel.textContent.trim();
          row.querySelector('[data-tt-gift-line-name]').textContent = data.ttGiftName;
          ['increase', 'decrease', 'remove'].forEach(action => {
            const control = row.querySelector('[data-tt-gift-' + action + ']');
            const key = 'ttGiftLabel' + action.charAt(0).toUpperCase() + action.slice(1);
            control.setAttribute('aria-label', this.dataset[key] + ': ' + name);
          });
          this.lines.appendChild(row);
        }
        row.querySelector('[data-tt-gift-line-price]').textContent = selection.quantity + ' × ' + selection.priceMoney;
        const quantityLabel = row.querySelector('[data-tt-gift-line-quantity]');
        quantityLabel.textContent = String(selection.quantity);
        quantityLabel.setAttribute('aria-label', this.dataset.ttGiftLabelQuantity + ': ' + selection.quantity);
        const increase = row.querySelector('[data-tt-gift-increase]');
        increase.disabled = selection.quantity >= 99;
        if (selection.quantity >= 99) increase.title = this.dataset.ttGiftLimit;
        else increase.removeAttribute('title');
        total += this.parseMoney(selection.priceMoney) * selection.quantity;
      });
      if (this.totalLabel) this.totalLabel.textContent = this.formatMoney(total);
      if (this.selectionPanel) this.selectionPanel.hidden = selections.length === 0;
      this.dataset.ttGiftSelected = String(selections.length > 0);
      if (this.summaryCount) {
        const count = selections.reduce((sum, selection) => sum + selection.quantity, 0);
        this.summaryCount.textContent = count ? count + ' ' + this.dataset.ttGiftLabelSelected : '';
      }
      this.updateAddButton();
    }

    readMoneyFormat() {
      const zero = this.dataset.ttGiftMoneyZero || '';
      const large = this.dataset.ttGiftMoneyLarge || '';
      const numberPattern = /\d(?:[\d.,\s'’]*\d)?/;
      const zeroMatch = zero.match(numberPattern);
      const largeMatch = large.match(numberPattern);
      if (zeroMatch && largeMatch) {
        const decimalMatch = zeroMatch[0].match(/([.,])(\d+)$/);
        const decimals = decimalMatch ? decimalMatch[2].length : 0;
        const decimal = decimalMatch ? decimalMatch[1] : '';
        const integer = decimals ? largeMatch[0].slice(0, -(decimals + 1)) : largeMatch[0];
        const groups = integer.split(/\D+/);
        const separator = integer.match(/\D+/);
        return {
          decimals, decimal, group: separator ? separator[0] : '',
          primary: groups.length > 1 ? groups[groups.length - 1].length : 3,
          secondary: groups.length > 2 ? groups[groups.length - 2].length : 3,
          prefix: large.slice(0, largeMatch.index),
          suffix: large.slice(largeMatch.index + largeMatch[0].length)
        };
      }
      let formatter;
      try {
        formatter = new Intl.NumberFormat(this.dataset.ttGiftLocale || undefined, {
          style: 'currency', currency: this.dataset.ttGiftCurrency
        });
      } catch (error) { formatter = new Intl.NumberFormat(); }
      const parts = formatter.formatToParts(1234567.89);
      return {
        formatter, decimals: formatter.resolvedOptions().maximumFractionDigits,
        decimal: (parts.find(part => part.type === 'decimal') || {}).value || '.',
        group: (parts.find(part => part.type === 'group') || {}).value || ''
      };
    }

    parseMoney(value) {
      const format = this.moneyFormat;
      const match = String(value).match(/\d(?:[\d.,\s'’]*\d)?/);
      if (!match) return 0;
      let number = match[0];
      if (format.group) number = number.split(format.group).join('');
      if (format.decimal && format.decimal !== '.') number = number.replace(format.decimal, '.');
      return Math.round(Number(number.replace(/\s/g, '')) * Math.pow(10, format.decimals)) || 0;
    }

    formatMoney(subunits) {
      const format = this.moneyFormat;
      const divisor = Math.pow(10, format.decimals);
      if (format.formatter) return format.formatter.format(subunits / divisor);
      let integer = String(Math.floor(subunits / divisor));
      if (format.group) {
        const groups = [];
        let size = format.primary;
        while (integer.length > size) {
          groups.unshift(integer.slice(-size));
          integer = integer.slice(0, -size);
          size = format.secondary;
        }
        groups.unshift(integer);
        integer = groups.join(format.group);
      }
      const fraction = format.decimals ? format.decimal + String(subunits % divisor).padStart(format.decimals, '0') : '';
      return format.prefix + integer + fraction + format.suffix;
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.guide && this.guide.open) {
        event.preventDefault();
        event.stopPropagation();
        this.guide.open = false;
        this.guide.querySelector('summary').focus();
        return;
      }
      const radio = event.target.closest('[role="radio"]');
      if (!radio || !this.contains(radio) || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
      const options = radio.hasAttribute('data-tt-gift-size') ? this.sizes : this.designs.filter(button => !button.hidden && !button.disabled);
      if (!options.length) return;
      let index = options.indexOf(radio);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = options.length - 1;
      else index = (index + (['ArrowLeft','ArrowUp'].includes(event.key) ? -1 : 1) + options.length) % options.length;
      event.preventDefault();
      options[index].click();
      options[index].focus();
    }

    getSelections() {
      if (!this.quantities) return [];
      return Array.from(this.quantities, ([id, quantity]) => {
        const button = this.designById.get(id);
        if (!this.isAvailable(button) || quantity < 1) return null;
        const data = button.dataset;
        return {
          id: Number(id), quantity,
          title: data.ttGiftSizeValue + ' / ' + data.ttGiftName,
          price: Number(data.ttGiftPriceValue),
          compare: Number(data.ttGiftCompareValue),
          priceMoney: data.ttGiftMoney,
          compareMoney: data.ttGiftCompareMoney
        };
      }).filter(Boolean);
    }

    getSelection() {
      return this.getSelections()[0] || null;
    }

    emitChange() {
      const selections = this.getSelections();
      this.dispatchEvent(new CustomEvent('tt:gift-bag-change', {
        bubbles: true,
        detail: {
          selections,
          selection: selections[0] || null,
          variantId: this.current ? Number(this.current.dataset.ttGiftVariant) : null,
          checked: selections.length > 0
        }
      }));
    }
  }

  customElements.define('tt-gift-bag', TTGiftBag);
})();

